import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  QuerySnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  documentId,
} from 'firebase/firestore'
import { firebaseDb } from './init'
import {
  IDpLesson,
  IDpLessonFulfilled,
  IDpLessonRaw,
  IDynamicPapersHistoryItem,
  IGenericError,
  ILessonVariantOpenText,
  MessageRole,
  TLessonVariant,
} from '../types'
import { createFirestoreQueryChunks } from '@/utils/firestore-database'
import { getDynamicPaperHistoryById } from './dynamic-papers'

const dpLessonsCollectionName = 'dpLessons'
const dpLessonsVariantsSubCollectionName = 'variants'

const dpLessonsCollectionRef = collection(firebaseDb, dpLessonsCollectionName)

const dpLessonsDocRef = (id: string) =>
  doc(firebaseDb, dpLessonsCollectionName, id)

export async function getDpLessonById(
  id: string,
): Promise<IDpLesson | undefined> {
  try {
    const docRef = dpLessonsDocRef(id)
    const docSnap = await getDoc(docRef)

    if (!docSnap.exists()) {
      return undefined
    }

    return {
      id: docSnap.id,
      ...(docSnap.data() as IDpLessonRaw),
    }
  } catch (error: unknown) {
    console.error(`error while trying to get dpLesson by id: ${id} :>> `, error)
    return undefined
  }
}

export async function getDpLessonsByTopicId(
  topicId: string,
): Promise<IDpLesson[]> {
  try {
    const q = query(
      dpLessonsCollectionRef,
      where('topicIds', 'array-contains', topicId),
    )
    const querySnapshot = await getDocs(q)

    const lessons: IDpLesson[] = []
    querySnapshot.forEach((doc) => {
      lessons.push({
        id: doc.id,
        ...(doc.data() as IDpLessonRaw),
      })
    })

    return lessons
  } catch (error: unknown) {
    console.error(
      `error while trying to get dpLessons by topicId: ${topicId} :>> `,
      error,
    )
    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get dpLessons by topicId`,
    )
  }
}

export async function setDpLesson(
  id: string,
  lesson: IDpLessonRaw,
): Promise<void> {
  try {
    const docRef = dpLessonsDocRef(id)
    await setDoc(
      docRef,
      {
        ...lesson,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    )
  } catch (error: unknown) {
    console.error(`error while trying to set dpLesson: ${id} :>> `, error)
    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to save dpLesson`,
    )
  }
}

export async function getDpLessonVariantsById<T = TLessonVariant>(
  lessonId: string,
): Promise<T[]> {
  try {
    const variantsSubCollectionRef = collection(
      firebaseDb,
      dpLessonsCollectionName,
      lessonId,
      dpLessonsVariantsSubCollectionName,
    )

    const querySnapshot = (await getDocs(
      variantsSubCollectionRef,
    )) as unknown as QuerySnapshot<T>

    const variants: T[] = querySnapshot.docs.map((doc) => {
      const variant: T = { id: doc.id, ...doc.data() }

      return variant
    })

    return variants
  } catch (error: unknown) {
    console.error(`error while trying to get dpLesson variants :>> `, {
      message: (error as IGenericError)?.message,
      error,
      lessonId,
    })

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get dpLesson variants`,
    )
  }
}

export async function updateDpLessonVariantById<T = TLessonVariant>(
  lessonId: string,
  variantId: string,
  updateObj: Partial<T>,
): Promise<void> {
  try {
    const variantDocRef = doc(
      firebaseDb,
      dpLessonsCollectionName,
      lessonId,
      dpLessonsVariantsSubCollectionName,
      variantId,
    )

    await updateDoc(variantDocRef, {
      ...updateObj,
      updatedAt: serverTimestamp(),
    })
  } catch (error: unknown) {
    console.error(`error while trying to update dpLesson variant by id :>> `, {
      message: (error as IGenericError)?.message,
      error,
      lessonId,
      variantId,
      updateObj,
    })

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to update dpLesson variant`,
    )
  }
}

export async function getDpLessonsByIds(
  ids: string[],
  syncDocsOrderWithIds = false,
): Promise<IDpLesson[]> {
  // Early return if no ids provided
  if (!ids.length) return []

  try {
    // Split ids into chunks due to Firestore "in" query limit (max 10 items)
    const chunks = createFirestoreQueryChunks(ids)

    // Execute all chunked queries in parallel
    const chunkResults = await Promise.all(
      chunks.map(async (chunk) => {
        // Create a query to fetch documents by their document IDs
        const q = query(
          collection(firebaseDb, 'dpLessons'),
          where(documentId(), 'in', chunk),
        )

        const snapshot = (await getDocs(q)) as QuerySnapshot<IDpLessonRaw>

        // Map Firestore documents
        return snapshot.docs.map((doc) => {
          const docData: IDpLesson = {
            id: doc.id,
            ...doc.data(),
          }

          return docData
        })
      }),
    )

    // Flatten results from all chunks into a single array
    let lessons = chunkResults.flat()

    // Firestore does NOT guarantee order, so we restore it based on input ids
    if (syncDocsOrderWithIds) {
      const lessonsMap = new Map(lessons.map((lesson) => [lesson.id, lesson]))
      lessons = ids
        .map((id) => lessonsMap.get(id))
        .filter((lesson): lesson is IDpLessonFulfilled => lesson !== undefined)
    }

    return lessons
  } catch (error: unknown) {
    console.error(`error while trying to get lessons by ids`, {
      message: (error as IGenericError)?.message,
      error,
      ids,
    })

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get lessons by ids.`,
    )
  }
}

export async function enrichDpLessonsWithMarksStats(
  paperId: string,
  lessons: IDpLesson[],
  withVariants: boolean = false,
  userId?: string,
): Promise<IDpLesson[]> {
  try {
    const updatedLessons: IDpLesson[] = await Promise.all(
      lessons.map(async (lesson) => {
        const variants = await getDpLessonVariantsById(lesson.id)

        const totalMarks: number = variants.reduce((acc: number, next) => {
          const marks = Number((next as ILessonVariantOpenText)?.marks)
          return acc + (Number.isFinite(marks) ? marks : 0)
        }, 0)

        const history: IDynamicPapersHistoryItem | undefined =
          await getDynamicPaperHistoryById(
            paperId,
            userId ? `${userId}-${lesson.id}` : `${lesson.id}`,
          )

        const earnedMarks = history?.earnedMarks || 0

        const updated: IDpLesson = {
          ...lesson,
          totalMarks,
          earnedMarks,
          hasMarking: !!history?.lastMarksUpdate,
          completed: earnedMarks > 0 && earnedMarks === totalMarks,
          wasAttempted:
            history?.messages?.some(
              (message) => message.role === MessageRole.User,
            ) ?? false,
          ...(withVariants ? { variants } : {}),
        }

        return updated
      }),
    )

    return updatedLessons
  } catch (error: unknown) {
    console.error(
      `error while trying to enrich dp lessons with marks stats :>> `,
      {
        message: (error as IGenericError)?.message,
        error,
        paperId,
        lessons,
      },
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get lessons marks stats`,
    )
  }
}
